import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Calendar } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ServiceAreas = () => {
  const [marketRents, setMarketRents] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);

  const areasInfo = {
    "Overland Park": {
      subtitle: "Johnson County's largest city",
      description: "Premier residential communities serving families and professionals in established neighborhoods.",
      features: ["High-end single family homes", "Excellent school districts", "Strong rental demand"],
    },
    "Leawood": {
      subtitle: "Premium Kansas market",
      description: "Luxury residential opportunities in one of Kansas's most affluent communities.",
      features: ["Luxury home rentals", "Executive relocations", "Premium amenities"],
    },
    "Lenexa": {
      subtitle: "Dynamic suburban community",
      description: "Thriving community with excellent amenities and convenient access to Kansas City.",
      features: ["Modern developments", "City Center attractions", "Great schools"],
    },
    "Olathe": {
      subtitle: "Growing family community",
      description: "Family-oriented neighborhoods with new construction and established communities.",
      features: ["New construction homes", "Family neighborhoods", "Strong employment base"],
    },
    "Shawnee": {
      subtitle: "Diverse Johnson County community",
      description: "Diverse housing options from starter homes to luxury properties with excellent parks.",
      features: ["Diverse property types", "Affordable luxury", "Community amenities"],
    },
    "Prairie Village": {
      subtitle: "Charming established community",
      description: "Tree-lined streets and strong sense of community in this sought-after Kansas city.",
      features: ["Walkable neighborhoods", "Strong community feel", "Quality housing stock"],
    },
    "Mission": {
      subtitle: "Convenient KC metro location",
      description: "Small-town feel with big-city convenience in northeast Johnson County.",
      features: ["Affordable rentals", "Easy commute access", "Local shopping"],
    },
    "Merriam": {
      subtitle: "Strategic Kansas location",
      description: "Centrally located with great access to employment centers and entertainment.",
      features: ["Central location", "Turkish Park amenities", "Growing rental market"],
    },
  };

  useEffect(() => {
    fetchMarketRents();
  }, []);

  const fetchMarketRents = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/market-rents`);
      setMarketRents(response.data);
      if (response.data.length > 0) {
        setLastUpdated(new Date(response.data[0].last_updated));
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching market rents:", error);
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "";
    return new Intl.DateTimeFormat('en-US', { 
      year: 'numeric', 
      month: 'long'
    }).format(date);
  };

  return (
    <section id="areas" className="py-14 sm:py-18 lg:py-22">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-foreground mb-4">
            Serving Kansas City Metro - Kansas Side
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto mb-4">
            Local expertise across Johnson County, Kansas. We know the neighborhoods, understand the markets, and
            deliver results property owners and tenants love.
          </p>
          {lastUpdated && (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span>Market data updated: {formatDate(lastUpdated)}</span>
            </div>
          )}
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {loading ? (
            <div className="col-span-full text-center py-12">
              <div className="text-muted-foreground">Loading market data...</div>
            </div>
          ) : (
            marketRents.map((rentData, index) => {
              const info = areasInfo[rentData.city] || {};
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ delay: index * 0.08, duration: 0.5 }}
                >
                  <Card
                    data-testid="service-area-card"
                    className="h-full shadow-[0_10px_30px_rgba(15,42,74,0.06)] hover:shadow-[0_16px_45px_rgba(15,42,74,0.10)] hover:bg-accent/5 transition-all group"
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between mb-2">
                        <Badge variant="outline" className="gap-1">
                          <MapPin className="h-3 w-3" />
                          {rentData.city}, {rentData.state}
                        </Badge>
                        <div className="text-right">
                          <div className="text-lg font-semibold font-display tabular-nums text-accent">
                            {rentData.avg_rent}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Avg Market Rent
                          </div>
                        </div>
                      </div>
                      <CardTitle className="font-display text-lg">
                        {info.subtitle}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {info.description}
                      </p>
                      <ul className="space-y-1.5">
                        {info.features?.map((feature, idx) => (
                          <li
                            key={idx}
                            className="text-xs text-foreground/70 flex items-start"
                          >
                            <span className="mr-2 text-accent">•</span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })
          )}
        </div>
      </div>
    </section>
  );
};

export default ServiceAreas;
